<template>
  <div class="cta">
    <h2 class="h4">{{ welcomeText.title }}</h2>
    <p class="cta__subtitle">{{ welcomeText.subtitle }}</p>
    <CtaBox
      title="Fahrzeug-Überprüfung"
      subtitle='Egal ob "Pickerl", Kauf-Überprüfung oder andere Services'
      :disabled="!user"
      disabledTooltip="Sie müssen sich zuerst einloggen"
      @click="showDialog"
    >
      <template #icon><IconCar aria-hidden="true" /></template>
    </CtaBox>

    <h3 class="cta__subsection__heading h5">weitere Services</h3>

    <CtaBox
      v-if="showRB"
      :slim="true"
      title="Rechtsberatung"
      subtitle="Finden Sie rechtliche Klarheit – mit unserer Beratung!"
      :disabled="!user"
      @click="navToRB"
    >
      <template #icon><IconParagraph aria-hidden="true" /></template>
    </CtaBox>
    <CtaBox
      v-if="showVB"
      :slim="true"
      title="Versicherungsberatung"
      subtitle="Versicherungen optimieren – Termin vereinbaren!"
      :disabled="!user"
      @click="navToVB"
    >
      <template #icon><IconInsurance aria-hidden="true" /></template>
    </CtaBox>
    <CtaBox
      v-if="showTB"
      :slim="true"
      title="Technische Beratung"
      subtitle="Technische Fragen klären – Expertenrat einholen!"
      :disabled="!user"
      @click="navToTB"
    >
      <template #icon><IconCar aria-hidden="true" /></template>
    </CtaBox>
    <CtaBox
      v-if="showReisebüro"
      :slim="true"
      title="Reisebüro"
      subtitle="Reise planen – Beratungstermin buchen!"
      :disabled="!user"
      disabledTooltip="Sie müssen sich zuerst einloggen"
      @click="navToReise"
    >
      <template #icon><IconIsland aria-hidden="true" /></template>
    </CtaBox>
    <el-dialog
      v-model="showNoMemberDialog"
      :show-close="false"
      custom-class="no-member"
      append-to-body
    >
      <NoMemberDialog @closeNoMemberDialog="closeDialog" @confirm="confirmNoMember" />
    </el-dialog>
    <el-dialog v-model="showStateDialog" :show-close="false" custom-class="state" append-to-body>
      <StateDialog @closeStateDialog="closeDialog" />
    </el-dialog>
  </div>
</template>

<script>
import { getExternalAppointmentBaseUrl, navigateTo } from '@/utils/misc.js'
import { LANDESVERBAND } from '@/utils/constants'
import { trackGa4Event } from '@/utils/tracking'
import { mapGetters } from 'vuex'

import CtaBox from './CtaBox.vue'
import IconCar from '@/components/icons/IconCar.vue'
import IconInsurance from '@/components/icons/IconInsurance.vue'
import IconParagraph from '@/components/icons/IconParagraph.vue'
import NoMemberDialog from './NoMemberDialog.vue'
import StateDialog from './StateDialog.vue'
import IconIsland from '../icons/IconIsland.vue'

export default {
  components: {
    CtaBox,
    IconCar,
    IconParagraph,
    IconInsurance,
    IconIsland,
    StateDialog,
    NoMemberDialog,
  },
  text: {
    // static
    first: {
      title: 'Willkommen, [[firstname]] [[lastname]]!',
      titleCompany: 'Willkommen',
      subtitle: 'Was können wir für Sie tun?',
    },
    second: {
      title: 'Was können wir für Sie tun?',
      subtitle: 'Egal ob Fahrzeug oder Urlaub, wir sind für Sie da',
    },
  },
  data() {
    return {
      showStateDialog: false,
      showNoMemberDialog: false,
    }
  },
  computed: {
    ...mapGetters(['user', 'userHasPaid', 'sessionData', 'userAccountInfo']),
    welcomeText() {
      // `userHasPaid` is set to `true` as default but can only be fetched once the user is logged in
      if (this.user && this.userHasPaid) {
        if (this.user.isCompany) {
          return {
            title: this.$options.text.first.titleCompany,
            subtitle: this.$options.text.first.subtitle,
          }
        }
        return {
          title: this.$options.text.first.title
            .replace('[[firstname]]', this.user.firstname)
            .replace('[[lastname]]', this.user.lastname),
          subtitle: this.$options.text.first.subtitle,
        }
      } else {
        return this.$options.text.second
      }
    },
    isWNB() {
      return this.userAccountInfo?.lv === LANDESVERBAND.WNB
    },
    isOOE() {
      return this.userAccountInfo?.lv === LANDESVERBAND.OOE
    },
    isWNBorOOE() {
      return this.isWNB || this.isOOE
    },
    showRB() {
      // Rechtsberatung: nur für WNB oder OÖ Mitglieder
      return this.user?.isMember && this.isWNBorOOE
    },
    showVB() {
      // Versicherungsberatung: nur für WNB oder OÖ
      return this.isWNBorOOE
    },
    showTB() {
      // Technische Beratung: nur für OÖ
      return this.isOOE
    },
    showReisebüro() {
      // always visible, disabled state handled via :disabled prop
      return true
    },
  },
  watch: {
    sessionData() {
      if (this.sessionData) {
        this.$store.dispatch('getLandesclubsTelephone')
        this.$store.dispatch('getFederalStates')
      }
    },
  },
  methods: {
    showDialog() {
      if (!this.user?.isMember) {
        this.showNoMemberDialog = true
      } else {
        this.showStateDialog = true
      }

      // User clicks on Termin buchen
      trackGa4Event({
        ga4Event: 'tvg_start',
        ga4ParameterName: 'action',
        ga4ParameterValue: 'start',
      })
    },
    confirmNoMember() {
      this.showNoMemberDialog = false
      this.showStateDialog = true
    },
    closeDialog() {
      this.showStateDialog = false
      this.showNoMemberDialog = false
    },
    navToRB() {
      navigateTo(getExternalAppointmentBaseUrl() + '/beratungstermine/rb')
    },
    navToVB() {
      navigateTo(getExternalAppointmentBaseUrl() + '/beratungstermine/vb')
    },
    navToTB() {
      navigateTo(getExternalAppointmentBaseUrl() + '/beratungstermine/tb')
    },
    navToReise() {
      navigateTo(getExternalAppointmentBaseUrl() + '/beratungstermine/reise')
    },
  },
}
</script>

<style lang="scss">
.cta {
  h4 {
    margin-bottom: 4px;
  }

  &__subtitle {
    @include subtitle;
  }

  .cta-box + .cta-box {
    margin-top: 16px;
  }

  &__subsection__heading {
    margin-top: 32px;
    margin-bottom: 16px;
  }
}
</style>
